/**
 * A drawn free-standing leg that lies along a wall is that wall's run.
 *
 * The draw tool ties a leg to a wall only when BOTH its clicks are on one
 * (`buildRunSegments`). A click where no wall is — a corner the walls stop
 * short of — makes both legs touching it free-standing, however close they
 * run to a wall: a back panel the length of each, across any opening in the
 * wall behind, and no uppers. Robinson/Mckinzie Scan 1's kitchen (2026-11-05):
 * the bottom and right walls both stop at an open diagonal, the run's third
 * click fell in that corner, and the bottom leg — 1.2" in front of its wall —
 * carried a 133.6" panel across a 36" opening.
 *
 * So a free leg within `ALONG_WALL_MAX_TURN_DEG` of parallel to a wall, with
 * both ends within `ALONG_WALL_MAX_GAP` of the wall's line, is moved onto that
 * line: the stretch the wall covers becomes a wall leg, and only what lies
 * past the wall's end stays free-standing, carrying straight on (the shape a
 * generated straight-on continuation has). Where a moved leg meets the leg
 * before or after it, the two meet where their lines cross, as two walls do
 * (`wallLineIntersection`).
 *
 * Both limits are the user's (2026-11-05, "those thresholds sound good to
 * start with"): 5° is the draw tool's own bend snap, 2" the run engine's
 * end-snap distance.
 *
 * Pure. Metres.
 */
const {
  getWallAxes,
  worldToWallParam,
  wallParamToSurfacePoint,
} = require('../kitchen-assembly/wallMath');
const {
  RUN_ENDPOINT_SNAP_TOLERANCE,
} = require('../kitchen-assembly/constants');
const { BEND_SNAP_DEG } = require('./bendGeometry');

const ALONG_WALL_MAX_TURN_DEG = BEND_SNAP_DEG;
const ALONG_WALL_MAX_GAP = RUN_ENDPOINT_SNAP_TOLERANCE;
// A wall overlap, or a free piece left over, shorter than this is a click
// artifact, not a leg — the draw tool's own free-stub threshold.
const MIN_PIECE = 0.05;
// How far a joint may move to meet where two lines cross. A leg moves at most
// ALONG_WALL_MAX_GAP; at a square corner that moves the joint under 3".
const MAX_JOINT_SHIFT = 0.3;

const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });
const dot = (a, b) => a.x * b.x + a.z * b.z;
const cross = (a, b) => a.x * b.z - a.z * b.x;
const along = (o, d, t) => ({ x: o.x + d.x * t, z: o.z + d.z * t });
const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
const unit = (a, b) => {
  const len = dist(a, b);
  return len > 0 ? { x: (b.x - a.x) / len, z: (b.z - a.z) / len } : null;
};

/** The wall's line: a point on it, its direction and its length. */
function wallLine(wall) {
  const axes = getWallAxes(wall);
  return axes ? { o: axes.start, d: axes.wallDir, len: axes.wallLen } : null;
}

/** Where along the wall `leg` overlaps it, or null when it does not lie along it. */
function overlapWith(leg, wall) {
  const line = wallLine(wall);
  const u = unit(leg.a, leg.b);
  if (!line || !u) return null;
  const maxSin = Math.sin((ALONG_WALL_MAX_TURN_DEG * Math.PI) / 180);
  if (Math.abs(cross(u, line.d)) > maxSin + 1e-12) return null;
  const off = (p) => Math.abs(cross(line.d, sub(p, line.o)));
  if (Math.max(off(leg.a), off(leg.b)) > ALONG_WALL_MAX_GAP + 1e-9) return null;
  const ta = dot(sub(leg.a, line.o), line.d);
  const tb = dot(sub(leg.b, line.o), line.d);
  const lo = Math.max(Math.min(ta, tb), 0);
  const hi = Math.min(Math.max(ta, tb), line.len);
  return hi - lo >= MIN_PIECE ? { wall, line, length: hi - lo } : null;
}

/**
 * One free leg as the pieces it lies along: wall pieces where walls cover it,
 * free pieces on the same line past their ends. Unchanged when no wall does.
 */
function piecesAlongWalls(leg, walls) {
  const hits = walls.map((w) => overlapWith(leg, w)).filter(Boolean);
  if (!hits.length) return [{ ...leg, moved: false }];
  const ref = hits.reduce((a, b) => (b.length > a.length ? b : a));
  // Only walls on the same line as the one it lies along most: two captured
  // pieces of one physical wall, not a parallel wall a few inches behind.
  const onLine = (p) =>
    Math.abs(cross(ref.line.d, sub(p, ref.line.o))) <= ALONG_WALL_MAX_GAP;
  const walled = hits.filter(
    (h) => onLine(h.line.o) && onLine(along(h.line.o, h.line.d, h.line.len))
  );
  // The leg moved onto that line.
  const project = (p) =>
    along(ref.line.o, ref.line.d, dot(sub(p, ref.line.o), ref.line.d));
  const a = project(leg.a);
  const b = project(leg.b);
  const u = unit(a, b);
  const len = dist(a, b);
  const at = (p) => Math.max(0, Math.min(len, dot(sub(p, a), u)));
  const spans = walled
    .map((h) => {
      const s0 = at(h.line.o);
      const s1 = at(along(h.line.o, h.line.d, h.line.len));
      return { wall: h.wall, from: Math.min(s0, s1), to: Math.max(s0, s1) };
    })
    .filter((s) => s.to - s.from >= MIN_PIECE)
    .sort((x, y) => x.from - y.from);
  const pieces = [];
  let cursor = 0;
  const push = (piece) => pieces.push({ ...piece, moved: true });
  for (const span of spans) {
    const from = Math.max(span.from, cursor);
    if (span.to - from < MIN_PIECE) continue;
    if (from - cursor >= MIN_PIECE) {
      push({ free: true, a: along(a, u, cursor), b: along(a, u, from) });
    } else if (pieces.length) {
      // A sliver between two walls: the wall before it reaches across.
      pieces[pieces.length - 1].b = along(a, u, from);
    }
    // A sliver before the first wall goes to that wall.
    const start = pieces.length ? from : Math.min(from, cursor);
    push({ wall: span.wall, a: along(a, u, start), b: along(a, u, span.to) });
    cursor = span.to;
  }
  if (len - cursor >= MIN_PIECE) {
    push({ free: true, a: along(a, u, cursor), b: along(a, u, len) });
  } else if (pieces.length) {
    pieces[pieces.length - 1].b = along(a, u, len);
  }
  return pieces.length ? pieces : [{ ...leg, moved: false }];
}

/** The line a piece lies on: its wall's, or its own. */
function pieceLine(piece) {
  if (piece.wall) {
    const line = wallLine(piece.wall);
    if (line) return { o: line.o, d: line.d };
  }
  const d = unit(piece.a, piece.b);
  return d ? { o: piece.a, d } : null;
}

function crossing(l1, l2) {
  const denom = cross(l1.d, l2.d);
  if (Math.abs(denom) < 1e-9) return null;
  const t = cross(sub(l2.o, l1.o), l2.d) / denom;
  return along(l1.o, l1.d, t);
}

/** Where a moved piece meets its neighbour: where their lines cross. */
function joinMovedPieces(pieces) {
  for (let i = 0; i + 1 < pieces.length; i += 1) {
    const p = pieces[i];
    const q = pieces[i + 1];
    if (!p.moved && !q.moved) continue;
    const lp = pieceLine(p);
    const lq = pieceLine(q);
    if (!lp || !lq) continue;
    const maxSin = Math.sin((ALONG_WALL_MAX_TURN_DEG * Math.PI) / 180);
    if (Math.abs(cross(lp.d, lq.d)) <= maxSin) continue; // in line: already met
    const x = crossing(lp, lq);
    if (
      !x ||
      dist(x, p.b) > MAX_JOINT_SHIFT ||
      dist(x, q.a) > MAX_JOINT_SHIFT
    ) {
      continue;
    }
    p.b = x;
    q.a = x;
  }
  return pieces;
}

const asLeg = (seg) =>
  seg.freestanding
    ? { free: true, a: seg.start, b: seg.end }
    : {
        wall: seg.wall,
        a: wallParamToSurfacePoint(seg.wall, seg.startParam),
        b: wallParamToSurfacePoint(seg.wall, seg.endParam),
      };

const asSegment = (piece) =>
  piece.free
    ? { freestanding: true, start: piece.a, end: piece.b }
    : {
        wall: piece.wall,
        startParam: worldToWallParam(piece.wall, piece.a),
        endParam: worldToWallParam(piece.wall, piece.b),
      };

/**
 * The drawn legs, with every free leg that lies along one of `walls` moved
 * onto it (see the file comment). Wall legs, and free legs along no wall,
 * come back as they were.
 *
 * @param {Array<Object>} segments  `buildRunSegments`' legs, in order
 * @param {Array<Object>} walls  the room's walls, resolved like the clicks'
 * @returns {Array<Object>} the same shape of legs
 */
function placeFreeLegsAlongWalls(segments, walls) {
  if (!walls?.length || !segments.some((s) => s.freestanding)) return segments;
  const pieces = [];
  segments.forEach((seg, i) => {
    const leg = asLeg(seg);
    if (!leg.a || !leg.b) {
      pieces.push({ ...leg, moved: false, keep: segments[i] });
      return;
    }
    if (seg.freestanding) pieces.push(...piecesAlongWalls(leg, walls));
    else pieces.push({ ...leg, moved: false, keep: seg });
  });
  if (!pieces.some((p) => p.moved)) return segments;
  joinMovedPieces(pieces);
  return pieces.map((p) =>
    p.keep &&
    dist(p.a, asLeg(p.keep).a) < 1e-9 &&
    dist(p.b, asLeg(p.keep).b) < 1e-9
      ? p.keep
      : asSegment(p)
  );
}

module.exports = {
  placeFreeLegsAlongWalls,
  ALONG_WALL_MAX_TURN_DEG,
  ALONG_WALL_MAX_GAP,
};
